import { useEffect, useMemo, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useParams,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useStoreWrapper, useViewStateWrapper } from "../../store/store";
import IncidentSideBar from "./sidebar";
import {
  IncidentGrid,
  Sidebar,
  Content,
  HideOnDesktop,
} from "./incident.style";
import { HiMenuAlt1 } from "react-icons/hi";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import CustomLoader from "../../components/CustomLoader";
import { stages } from "../../utils/helpers/incidentFormTabs";
import { getInitialValues } from "../../utils/formGenUtils/getInitialValues";
import {
  IFieldInstance,
  IncidentStatus,
  TabOptions,
} from "../../types/fieldInstance.interface";
import { tabSeparatedData } from "../../utils/formGenUtils/getTabSeparatedData";
import {
  doesUrlEndWith,
  getDraftId,
  handleLinkClick,
  handleLogoClick,
  setFormState,
  updateFormStateOnClick,
} from "./methods";
import useFetchedIncident from "../../hooks/useFetchedIncident";
import useIncident from "../../hooks/useIncident";
import { viewClickedIncident } from "../../utils/helpers/incidentListUtils";
import {
  INCIDENT_ORIGIN_STATE_KEY,
  getSafeReturnPath,
} from "../../utils/helpers/incidentNavigation";
import useIncidentTypes from "../../hooks/useIncidentTypes";
const IncidentLayout = () => {
  const contentRef = useRef();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const params = useParams();
  const formId = params.incidentIdOrNew || "";
  useEffect(() => {
    //@ts-ignore
    contentRef.current?.scrollTo({
      top: 0,
      left: 0,
    });
  }, [location.pathname]);
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [incidentTypeObj, setIncidentTypeObj] = useState<
    { id: string; label: string } | undefined
  >();
  const [shouldNavigateToApprovals, setShouldNavigateToApprovals] =
    useState(false);
  const [showAllegedAbuseFlag, setShowAllegedAbuseFlag] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen((prevState) => !prevState);
  };

  const { forms, createForm, updateForm, userId, setAttachments } =
    useStoreWrapper(
      (state) => ({
        userId: state.user.userId,
        forms: state.forms,
        createForm: state.createForm,
        updateForm: state.updateForm,
        setAttachments: state.setAttachments,
      }),
      shallow
    );

  const currentState = forms[formId];
  const incidentStatus = currentState?.formState?.incidentStatus;
  const draftId = currentState?.formState?.draftId;
  const hasUnsavedChanges = currentState?.formState?.hasUnsavedChanges;
  const tabState = currentState?.formState.tabState;
  const facilityId = currentState?.baseFormData?.facilityId;
  const idempotencyKey = currentState?.baseFormData?.idempotencyKey;
  const isIncidentDraft = currentState
    ? incidentStatus === IncidentStatus.Draft
    : !!formId && !formId.startsWith("a02") && formId.length === 18;
  const isNewIncident = currentState
    ? !incidentStatus
    : !!formId && !formId.startsWith("a02");
  const isIncidentSubmitted = !isIncidentDraft && !isNewIncident;
  const draftID = getDraftId(isIncidentDraft, formId, draftId);

  const {
    createIncidentEntry: {
      isLoading: createIncidentLoading,
      mutateAsync: createMutateAsync,
      isSuccess: createOrUpdateSuccess,
    },
    saveIncidentAsDraft: {
      isLoading: saveIncidentLoading,
      mutateAsync: saveMutateAsync,
      isSuccess: saveOrUpdateSuccess,
    },
    deleteIncidentDraft: {
      isLoading: deleteIncidentLoading,
      mutateAsync: deleteMutateAsync,
    },
    deleteIncident: {
      isLoading: isDeletingIncident,
      mutate: handleDeleteIncident,
    },
    getIncident,
    getDraft,
    setStatusToOpen: {
      isLoading: setStatusLoading,
      mutateAsync: setStatusAsync,
      isSuccess: setStatusSuccess,
    },
    restoreIncident,
  } = useIncident({
    facilityId: facilityId,
    idempotencyKey: !isIncidentSubmitted ? idempotencyKey : "",
    incidentId: isIncidentSubmitted ? formId : "",
    draftId: draftID,
  });

  // Track each successful submit by incrementing on the false -> true transition
  // of the mutation success flag so downstream logic can react to every success.
  const [submitSuccessCount, setSubmitSuccessCount] = useState(0);
  const prevCreateSuccessRef = useRef(createOrUpdateSuccess);
  useEffect(() => {
    if (createOrUpdateSuccess && !prevCreateSuccessRef.current) {
      setSubmitSuccessCount((prev) => prev + 1);
    }
    prevCreateSuccessRef.current = createOrUpdateSuccess;
  }, [createOrUpdateSuccess]);

  const { selectedIncident, isIncidentLoading, isDataError } =
    useFetchedIncident({
      formId,
      createOrUpdateSuccess,
      saveOrUpdateSuccess,
      getIncident,
      getDraft,
      isIncidentDraft,
      setStatusSuccess,
    });

  const { incidentTypes: incidentTypesObject } = useIncidentTypes();

  useEffect(() => {
    if (!userId && !isIncidentLoading && !isDataError && !selectedIncident) {
      navigate("/");
    } else if (isDataError) {
      setIsLoading(false);
    } else if (selectedIncident && incidentTypesObject.length) {
      const incidentType = incidentTypesObject.find(
        (incident: any) => incident.flag === selectedIncident.RecordTypeId
      );
      setIncidentTypeObj(incidentType);
      if (!currentState) {
        viewClickedIncident(
          createForm,
          updateForm,
          selectedIncident,
          incidentType,
          navigate,
          false
        );
      }
      setIsLoading(false);
    } else if (isNewIncident) {
      const incidentType = incidentTypesObject.find(
        (incident: any) =>
          incident.id === currentState?.baseFormData?.incidentType
      );
      setIncidentTypeObj(incidentType);
      setIsLoading(false);
    }
  }, [
    selectedIncident,
    isIncidentLoading,
    isDataError,
    userId,
    incidentTypesObject,
    currentState,
  ]);

  useEffect(() => {
    const _showAllegedAbuseFlag =
      incidentTypeObj?.id === "AA-1" && isNewIncident;
    setShowAllegedAbuseFlag(_showAllegedAbuseFlag);
  }, [incidentTypeObj?.id]);

  useEffect(() => {
    return () => {
      setAttachments([]);
    };
  }, []);

  // Router state is dropped by in-incident tab navigation, so capture it once at entry.
  const { incidentReturnTo, setIncidentReturnTo } = useViewStateWrapper(
    (state) => ({
      incidentReturnTo: state.incidentReturnTo,
      setIncidentReturnTo: state.setIncidentReturnTo,
    }),
    shallow
  );
  useEffect(() => {
    setIncidentReturnTo(
      getSafeReturnPath(location.state?.[INCIDENT_ORIGIN_STATE_KEY])
    );
  }, []);

  const initialValues = useMemo(
    () =>
      getInitialValues(
        Object.values(tabSeparatedData).flat() as IFieldInstance[]
      ),
    [tabSeparatedData]
  );

  const allFields = useMemo(
    () => Object.values(tabSeparatedData).flat() as IFieldInstance[],
    [tabSeparatedData]
  );

  const _setFormState = setFormState(formId, updateForm);

  function setIncidentFormData(values: any) {
    _setFormState({
      type: "UPDATE_INCIDENT_FORM_DATA",
      payload: values,
    });
  }
  function setIncidentFormState(values: any) {
    _setFormState({
      type: "UPDATE_FORM_STATE",
      payload: values,
    });
  }
  // Merge per-tab into the latest tabState (never a full-object replace from a
  // stale snapshot) so parallel validity writes don't clobber each other.
  function mergeIncidentTabState(payload: { [tab: string]: any }) {
    _setFormState({ type: "MERGE_TAB_STATE", payload });
  }
  function setDeleteRLRecordObject(values: any) {
    _setFormState({
      type: "UPDATE_DELETE_RL_RECORD",
      payload: values,
    });
  }

  function resetDeleteRLRecordObject() {
    _setFormState({
      type: "RESET_DELETE_RL_RECORD",
    });
  }

  const _updateFormStateOnClick = updateFormStateOnClick(_setFormState);

  function onLogoClick() {
    const approvalStage = stages.find(
      (stage) => stage.label === TabOptions.Approvals
    );
    const isApprovalsInactive = approvalStage
      ? !doesUrlEndWith(location.pathname, approvalStage.path)
      : false;
    const shouldShowPrompt = hasUnsavedChanges && isApprovalsInactive;
    currentState
      ? handleLogoClick(
          navigate,
          _setFormState,
          shouldShowPrompt,
          incidentReturnTo
        )
      : navigate(incidentReturnTo ?? "/");
  }

  async function onLinkClick(path: string, label: string) {
    const mode = searchParams.get("mode");
    const nonFormTabs = [TabOptions.Approvals, TabOptions.Attachments];
    const isNotFormTab = nonFormTabs.includes(label as TabOptions);
    const updatedMode = !isNotFormTab ? mode : "view";
    const searchParameters = mode ? `?mode=${updatedMode}` : "";
    const formattedPath =
      `/incidents/${params.incidentIdOrNew}/${path}` + searchParameters;
    hasUnsavedChanges && isNotFormTab && setShouldNavigateToApprovals(true);
    handleLinkClick(
      formattedPath,
      _updateFormStateOnClick,
      navigate,
      label,
      hasUnsavedChanges,
      _setFormState,
      isNotFormTab
    );
  }

  const shouldShowApprovals =
    !!incidentStatus && incidentStatus !== IncidentStatus.Draft;

  return (
    <>
      <IncidentGrid>
        <Sidebar>
          <IncidentSideBar
            onLogoClick={onLogoClick}
            onLinkClick={onLinkClick}
            tabState={tabState}
            onSelectItem={() => setIsDrawerOpen(false)}
            showApprovalsSection={shouldShowApprovals}
          />
        </Sidebar>
        {/* @ts-ignore */}
        <Content ref={contentRef}>
          <HideOnDesktop>
            <HiMenuAlt1
              size={40}
              style={{ cursor: "pointer" }}
              onClick={() => setIsDrawerOpen(true)}
            />
          </HideOnDesktop>
          <Drawer
            open={isDrawerOpen}
            onClose={toggleDrawer}
            direction="left"
            style={{ width: "fit-content", minWidth: "300px", zIndex: "1020" }}
            zIndex={1015}
          >
            <div
              className="flex h-screen w-full flex-col px-7 py-7"
              style={{ background: "#3d0052", height: "100vh" }}
            >
              <IncidentSideBar
                onLogoClick={onLogoClick}
                onLinkClick={onLinkClick}
                tabState={tabState}
                onSelectItem={() => setIsDrawerOpen(false)}
                showApprovalsSection={shouldShowApprovals}
              />
            </div>
          </Drawer>
          {isLoading ? (
            <CustomLoader fullscreen />
          ) : (
            <Outlet
              context={{
                userId,
                tabSeparatedData,
                initialValues,
                allFields,
                createIncidentLoading,
                createMutateAsync,
                submitSuccessCount,
                saveIncidentLoading,
                saveMutateAsync,
                deleteIncidentLoading,
                deleteMutateAsync,
                isDeletingIncident,
                handleDeleteIncident,
                restoreIncident,
                selectedIncident,
                isIncidentLoading,
                isDataError,
                incidentTypeObj,
                shouldNavigateToApprovals,
                showAllegedAbuseFlag,
                setShowAllegedAbuseFlag,
                setShouldNavigateToApprovals,
                setIncidentFormData,
                setIncidentFormState,
                mergeIncidentTabState,
                setDeleteRLRecordObject,
                resetDeleteRLRecordObject,
                setStatusLoading,
                setStatusAsync,
              }}
            />
          )}
        </Content>
      </IncidentGrid>
    </>
  );
};

export default IncidentLayout;

export function useIncidentData() {
  type IncidentFormContext = {
    userId: string;
    tabSeparatedData: any;
    initialValues: { [x: string]: any };
    allFields: IFieldInstance[];
    createIncidentLoading: boolean;
    createMutateAsync: any;
    submitSuccessCount: number;
    saveIncidentLoading: boolean;
    saveMutateAsync: any;
    deleteIncidentLoading: boolean;
    deleteMutateAsync: any;
    selectedIncident: any;
    isIncidentLoading: boolean;
    isDataError: boolean;
    incidentTypeObj: any;
    shouldNavigateToApprovals: boolean;
    isDeletingIncident: boolean;
    handleDeleteIncident: any;
    restoreIncident: any;
    showAllegedAbuseFlag: boolean;
    setShowAllegedAbuseFlag: (x: boolean) => void;
    setShouldNavigateToApprovals: (x: boolean) => void;
    setIncidentFormData: (x: any) => void;
    setIncidentFormState: (x: any) => void;
    mergeIncidentTabState: (x: { [tab: string]: any }) => void;
    setDeleteRLRecordObject: (x: any) => void;
    resetDeleteRLRecordObject: () => void;
    setStatusLoading: boolean;
    setStatusAsync: any;
  };
  return useOutletContext<IncidentFormContext>();
}
